// ABOUTME: Memory-efficient UUID generation utilities for database records
// ABOUTME: Provides compact yet unique identifiers to replace auto-incrementing integer IDs

/**
 * Generates a memory-efficient UUID using a combination of timestamp and random values
 * Format: 8 chars timestamp + 4 chars random = 12 character ID
 * This provides uniqueness while being more compact than full UUIDs
 */
export function generateCompactUUID(): string {
    // Get current timestamp in base36 (more compact than base10)
    const timestamp = Date.now().toString(36);
    
    // Generate 4 random characters in base36
    const random = Math.random().toString(36).substring(2, 6);
    
    // Combine for a compact but unique ID
    return `${timestamp}${random}`;
}

/**
 * Generates a standard UUID v4 for cases where full UUID compatibility is needed
 */
export function generateStandardUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * Validates if a string is a valid compact UUID format
 */
export function isValidCompactUUID(id: string): boolean {
    // Should be 12 characters, alphanumeric
    return /^[a-z0-9]{12}$/.test(id);
}

/**
 * Validates if a string is a valid standard UUID format
 */
export function isValidStandardUUID(id: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
}

/**
 * Converts an integer ID to a compact UUID format (for migration purposes)
 * This is useful when transitioning from integer IDs to UUIDs
 */
export function migrateIntegerToUUID(integerId: number): string {
    // Use the integer as part of the timestamp component for consistency
    const paddedId = integerId.toString(36).padStart(8, '0');
    const random = Math.random().toString(36).substring(2, 6);
    return `${paddedId}${random}`;
}

/**
 * Default ID generator for the application
 * Uses compact UUIDs for better performance and storage efficiency
 */
export const generateId = generateCompactUUID;